// src/components/HUD/HUDSourceDataPanel.js
// UPDATED: 2026-08-09 — changed /admin/hud/upload-source-data to /hud/upload-source-data
// so hudToken users can upload source data.
import React, { useState, useRef } from 'react';
import axios from 'axios';
const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';
export default function HUDSourceDataPanel({ onUploadComplete }) {
  const [uploading,    setUploading]    = useState(false);
  const [progress,     setProgress]     = useState(0);
  const [result,       setResult]       = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  function handleFileChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    setResult(null);
  }
  async function handleUpload() {
    if (!selectedFile) return;
    setUploading(true);
    setProgress(10);
    setResult(null);
    const formData = new FormData();
    formData.append('sourceData', selectedFile);
    try {
      setProgress(30);
      const res = await axios.post(
        `${API_URL}/hud/upload-source-data`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded / e.total) * 60);
            setProgress(30 + pct);
          },
        }
      );
      setProgress(100);
      const data = res.data;
      setResult({
        type:       'success',
        message:    data.message,
        phaCount:   data.phaCount,
        created:    data.created,
        updated:    data.updated,
        fileName:   data.fileName,
        uploadedAt: new Date().toLocaleString(),
      });
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (onUploadComplete) onUploadComplete(data);
    } catch (err) {
      setProgress(0);
      const msg = err?.response?.data?.message || err.message || 'Upload failed.';
      setResult({ type: 'error', message: msg });
    } finally {
      setUploading(false);
      setTimeout(() => setProgress(0), 800);
    }
  }
  return (
    <div style={{
      background: '#ffffff', border: '1px solid #D3D1C7',
      borderRadius: '10px', padding: '16px 20px', marginBottom: '16px',
    }}>
      <div style={{ marginBottom: '12px' }}>
        <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#1a1a18' }}>
          Source Data Upload
        </p>
        <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#888780' }}>
          Upload Oracle FDS export (Source_Data.xlsx) to populate PHA records
        </p>
      </div>
      {result?.type === 'success' && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
          {[
            { value: result.phaCount, label: 'PHAs processed', color: '#534AB7' },
            { value: result.created,  label: 'New records',    color: '#534AB7' },
            { value: result.updated,  label: 'Updated',        color: '#0F6E56' },
          ].map((s, i) => (
            <div key={i} style={{ background: '#F7F6F2', borderRadius: '8px', padding: '8px 14px', minWidth: '100px' }}>
              <div style={{ fontSize: '20px', fontWeight: '700', color: s.color, lineHeight: 1.1 }}>{s.value}</div>
              <div style={{ fontSize: '10px', color: '#888780', marginTop: '2px' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <button
          onClick={() => !uploading && fileInputRef.current?.click()}
          disabled={uploading}
          style={{
            background: '#F0EFF8', color: '#534AB7', border: '1.5px solid #534AB7',
            borderRadius: '7px', padding: '7px 14px', fontSize: '12px', fontWeight: '600',
            cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1,
          }}
        >
          Choose File
        </button>
        <span style={{ fontSize: '11px', color: '#888780', fontStyle: 'italic' }}>
          {selectedFile ? selectedFile.name : 'No file selected'}
        </span>
        <button
          onClick={handleUpload}
          disabled={uploading || !selectedFile}
          style={{
            background: uploading || !selectedFile ? '#C4C2D8' : '#534AB7',
            color: '#fff', border: 'none', borderRadius: '7px', padding: '7px 16px',
            fontSize: '12px', fontWeight: '600',
            cursor: uploading || !selectedFile ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}
        >
          {uploading ? (
            <>
              <span style={{
                width: '10px', height: '10px', border: '2px solid #fff',
                borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block',
                animation: 'hudSpin 0.7s linear infinite',
              }} />
              Uploading...
            </>
          ) : <>&#8593; Upload Source Data</>}
        </button>
      </div>
      {progress > 0 && progress < 100 && (
        <div style={{ width: '100%', height: '3px', background: '#E2E0D8', borderRadius: '2px', marginTop: '10px', overflow: 'hidden' }}>
          <div style={{ height: '100%', background: '#534AB7', borderRadius: '2px', width: `${progress}%`, transition: 'width 0.3s ease' }} />
        </div>
      )}
      {result && (
        <div style={{
          marginTop: '10px', padding: '8px 12px', borderRadius: '7px', fontSize: '12px', lineHeight: '1.5',
          background: result.type === 'success' ? '#E1F5EE' : '#FCEBEB',
          color:      result.type === 'success' ? '#0F6E56' : '#A32D2D',
          border:     `1px solid ${result.type === 'success' ? '#0F6E56' : '#A32D2D'}`,
        }}>
          {result.message}
          {result.type === 'success' && result.uploadedAt && (
            <div style={{ marginTop: '3px', fontSize: '10px', opacity: 0.75 }}>
              File: {result.fileName} &nbsp;&bull;&nbsp; {result.uploadedAt}
            </div>
          )}
        </div>
      )}
      <style>{`@keyframes hudSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}